import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import aiHelmetImg from '@/assets/images/ai-helmet.webp';
import { Sparkles, ExportCurve, ArrowRight, type IconComponent } from '@/components/icons';
import { ConsolidationReviewModal } from '@/components/modals/ConsolidationReviewModal';
import { PublisherResultImportModal } from '@/components/publisher-results/PublisherResultImportModal';
import { AppDialogLayout } from '@/components/system';
import { useModalContext } from '@/components/ui/modal-provider';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useReportWizard } from '@/pages/create-report/context/ReportWizardContext';
import type { WizardStepProps } from './types';
import type { PublisherResultImportApprovalResult } from '@/api/publisher-result-imports';
import type { PublisherResultPatientApprovalImport } from '@/types/publisher-result-import';
import type { PendingConsolidation } from '@/types/report-payload';

type PendingResultsReviewGatewayProps = {
    pendingConsolidationsCount: number;
    pendingPublisherApprovalsCount: number;
    onReviewConsolidations: () => void;
    onReviewPublisherResults: () => void;
};

const pluralize = (count: number, singular: string, plural: string) =>
    count === 1 ? singular : plural;

type PendingReviewOptionCardProps = {
    icon: IconComponent;
    title: string;
    count: number;
    description: string;
    onClick: () => void;
};

function PendingReviewOptionCard({
    icon: Icon,
    title,
    count,
    description,
    onClick,
}: PendingReviewOptionCardProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="group flex w-full items-center justify-between gap-4 rounded-2xl border border-border bg-card p-4 text-left transition-all hover:-translate-y-0.5 hover:border-blue-200 hover:bg-surface hover:shadow-md"
        >
            <div className="flex min-w-0 items-start gap-3">
                <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-surface text-blue-600 shadow-sm">
                    <Icon className="size-5" />
                </div>
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="text-sm font-semibold text-foreground">{title}</div>
                    </div>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                        {description}
                    </p>
                </div>
            </div>
            <ArrowRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-blue-500" />
        </button>
    );
}

function PendingResultsReviewGateway({
    pendingConsolidationsCount,
    pendingPublisherApprovalsCount,
    onReviewConsolidations,
    onReviewPublisherResults,
}: PendingResultsReviewGatewayProps) {
    const totalCount = pendingConsolidationsCount + pendingPublisherApprovalsCount;
    const hasConsolidations = pendingConsolidationsCount > 0;
    const hasPublisherApprovals = pendingPublisherApprovalsCount > 0;
    const headline =
        hasConsolidations && !hasPublisherApprovals
            ? `Enquanto você estava fora, nossa IA corrigiu ${pendingConsolidationsCount} ${pluralize(
                  pendingConsolidationsCount,
                  'instrumento',
                  'instrumentos',
              )} para você`
            : !hasConsolidations && hasPublisherApprovals
              ? `Enquanto você estava fora, nossa IA preparou ${pendingPublisherApprovalsCount} ${pluralize(
                    pendingPublisherApprovalsCount,
                    'resultado importado',
                    'resultados importados',
                )} para você`
              : `Enquanto você estava fora, nossa IA preparou ${totalCount} resultados para você`;
    const description = null;

    return (
        <div className="overflow-hidden rounded-3xl">
            <div className="px-6 py-8 text-center sm:px-8">
                <img
                    src={aiHelmetImg}
                    alt=""
                    aria-hidden="true"
                    className="mx-auto mb-6 size-40 object-contain"
                />

                <h2 className="mx-auto max-w-md text-xl font-bold leading-snug text-foreground">
                    {headline}
                </h2>
                {description && (
                    <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
                        {description}
                    </p>
                )}

                <div className="mt-7 grid gap-3 text-left">
                    {hasConsolidations && (
                        <PendingReviewOptionCard
                            icon={Sparkles}
                            title="Instrumentos respondidos"
                            count={pendingConsolidationsCount}
                            description="Resultados corrigidos por IA para adicionar ao laudo."
                            onClick={onReviewConsolidations}
                        />
                    )}

                    {hasPublisherApprovals && (
                        <PendingReviewOptionCard
                            icon={ExportCurve}
                            title="PDFs importados"
                            count={pendingPublisherApprovalsCount}
                            description="Tabelas extraídas dos PDFs para aprovar e inserir no laudo."
                            onClick={onReviewPublisherResults}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

type PendingResultsReviewView = 'menu' | 'consolidations' | 'publisher';

type PendingResultsReviewModalProps = {
    pendingConsolidations: PendingConsolidation[];
    pendingPublisherApprovals: PublisherResultPatientApprovalImport[];
    reportData: WizardStepProps['data'];
    patientId: string;
    reportId: string;
    onAcceptConsolidation: (consolidation: PendingConsolidation) => void;
    onBeforeApprove: () => Promise<void>;
    onPublisherApproved: (result: PublisherResultImportApprovalResult) => Promise<void>;
    onCompleted: () => void;
    onClose: () => void;
};

function PendingResultsReviewModal({
    pendingConsolidations,
    pendingPublisherApprovals,
    reportData,
    patientId,
    reportId,
    onAcceptConsolidation,
    onBeforeApprove,
    onPublisherApproved,
    onCompleted,
    onClose,
}: PendingResultsReviewModalProps) {
    const [view, setView] = useState<PendingResultsReviewView>('menu');
    const [consolidationQueue, setConsolidationQueue] = useState(pendingConsolidations);
    const [activeConsolidations, setActiveConsolidations] = useState(pendingConsolidations);
    const [publisherQueue, setPublisherQueue] = useState(pendingPublisherApprovals);
    const [publisherIndex, setPublisherIndex] = useState(0);
    const currentPublisherImport = publisherQueue[publisherIndex];

    const openPublisherReview = () => {
        const firstReviewableIndex = publisherQueue.findIndex((record) => record.batch_id);

        if (firstReviewableIndex < 0) {
            toast.warning('Esta importação não possui lote para revisão.');
            return;
        }

        setPublisherIndex(firstReviewableIndex);
        setView('publisher');
    };

    const openConsolidationReview = () => {
        setActiveConsolidations(consolidationQueue);
        setView('consolidations');
    };

    const handleAcceptConsolidation = (consolidation: PendingConsolidation) => {
        onAcceptConsolidation(consolidation);
        setConsolidationQueue((current) =>
            current.filter((record) => record.id !== consolidation.id),
        );
    };

    const handlePublisherApproved = (importId: string) => {
        const nextQueue = publisherQueue.filter((record) => record.id !== importId);

        setPublisherQueue(nextQueue);

        if (nextQueue.length === 0) {
            setPublisherIndex(0);
            if (consolidationQueue.length === 0) {
                onClose();
            } else {
                setView('menu');
            }
            return;
        }

        setPublisherIndex((index) => Math.min(index, nextQueue.length - 1));
    };

    const handleConsolidationReviewCompleted = () => {
        if (publisherQueue.length > 0) {
            setView('menu');
            return;
        }

        onClose();
    };

    if (view === 'consolidations') {
        return (
            <ConsolidationReviewModal
                consolidations={activeConsolidations}
                reportData={reportData}
                onAccept={handleAcceptConsolidation}
                onClose={onClose}
                skipIntro
                onBackToMenu={() => setView('menu')}
                onCompleted={handleConsolidationReviewCompleted}
            />
        );
    }

    if (view === 'publisher' && currentPublisherImport?.batch_id) {
        return (
            <PublisherResultImportModal
                key={currentPublisherImport.id}
                patientId={patientId}
                mode="report"
                sourceReportId={reportId}
                initialBatchId={currentPublisherImport.batch_id}
                initialImportId={currentPublisherImport.id}
                reportData={reportData}
                onBeforeApprove={onBeforeApprove}
                onApproved={onPublisherApproved}
                onCompleted={onCompleted}
                onBackToMenu={() => setView('menu')}
                reviewNavigation={{
                    currentIndex: publisherIndex,
                    total: publisherQueue.length,
                    onPrevious: () => setPublisherIndex((index) => Math.max(0, index - 1)),
                    onNext: () =>
                        setPublisherIndex((index) =>
                            Math.min(publisherQueue.length - 1, index + 1),
                        ),
                    onSelect: setPublisherIndex,
                }}
                onReviewApproved={handlePublisherApproved}
                onClose={onClose}
            />
        );
    }

    return (
        <AppDialogLayout
            title="Resultados prontos para revisar"
            closeAction={{
                label: 'Fechar',
                onClick: onClose,
            }}
        >
            <PendingResultsReviewGateway
                pendingConsolidationsCount={consolidationQueue.length}
                pendingPublisherApprovalsCount={publisherQueue.length}
                onReviewConsolidations={openConsolidationReview}
                onReviewPublisherResults={openPublisherReview}
            />
        </AppDialogLayout>
    );
}

/**
 * Opens the publisher-result import modal on the current report.
 *
 * Shared by the toolbox card that starts an import and by the dock that tracks
 * one, so both land in the same modal with the same wiring. Pass the ids of an
 * existing import to open straight on it — that is how the dock sends the
 * psychologist to a duplicate that is waiting on a decision.
 */
export function usePublisherResultImportModal(reportData: WizardStepProps['data']) {
    const { openWizardModal, closeModal } = useModalContext();
    const queryClient = useQueryClient();
    const { reportId, preparePublisherResultApproval, handlePublisherResultApproved } =
        useReportWizard();

    const patientId = reportData?.patient?.id;
    const activeReportId = reportId || reportData?.id || null;

    const openPublisherResultImport = (
        initial: { batchId?: string | null; importId?: string | null } = {},
    ) => {
        if (!patientId || !activeReportId) return;

        openWizardModal({
            title: null,
            size: 'xl',
            className: 'max-h-[95vh]',
            showCloseButton: false,
            content: (
                <PublisherResultImportModal
                    patientId={patientId}
                    mode="report"
                    sourceReportId={activeReportId}
                    initialBatchId={initial.batchId ?? null}
                    initialImportId={initial.importId ?? null}
                    reportData={reportData}
                    onBeforeApprove={preparePublisherResultApproval}
                    onApproved={handlePublisherResultApproved}
                    onCompleted={() =>
                        queryClient.invalidateQueries({ queryKey: ['publisher-result-imports'] })
                    }
                    onClose={closeModal}
                />
            ),
        });
    };

    return { openPublisherResultImport };
}

/**
 * Opens the "resultados prontos para revisar" queue.
 *
 * Lives in a hook because two very different places need the same modal: the
 * chip inside the Resultados step, and the imports dock that floats over the
 * whole editor. Everything it needs already hangs off the wizard context, so
 * the caller only has to hand over the report it is editing.
 */
export function usePendingResultsReview(reportData: WizardStepProps['data']) {
    const { openModal, closeModal } = useModalContext();
    const queryClient = useQueryClient();
    const {
        reportId,
        pendingConsolidations,
        pendingPublisherResultApprovalImports,
        acceptConsolidation,
        preparePublisherResultApproval,
        handlePublisherResultApproved,
    } = useReportWizard();
    const publisherResultImportsFeature = useFeatureFlag('publisherResultImports.reviewFlow');

    const patientId = reportData?.patient?.id;
    const activeReportId = reportId || reportData?.id || null;

    const openPendingResultsReview = () => {
        if (!patientId || !activeReportId) return;

        openModal({
            title: null,
            size: 'xl',
            bodyClassName: 'overflow-hidden flex flex-col min-h-0 p-0',
            showCloseButton: false,
            content: (
                <PendingResultsReviewModal
                    pendingConsolidations={pendingConsolidations}
                    pendingPublisherApprovals={
                        publisherResultImportsFeature.enabled
                            ? pendingPublisherResultApprovalImports
                            : []
                    }
                    reportData={reportData}
                    patientId={patientId}
                    reportId={activeReportId}
                    onAcceptConsolidation={acceptConsolidation}
                    onBeforeApprove={preparePublisherResultApproval}
                    onPublisherApproved={handlePublisherResultApproved}
                    onCompleted={() =>
                        queryClient.invalidateQueries({ queryKey: ['publisher-result-imports'] })
                    }
                    onClose={closeModal}
                />
            ),
        });
    };

    return { openPendingResultsReview };
}
